import React, { FC, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import Grid from "@mui/material/Grid2";
import { Typography } from "@mui/material";
import Select from "@/components/Select/index";
import { useParams } from "react-router-dom";
import { useDisenroll } from "@/hooks/useEnrollments";
import { useCategoryByType } from "@/hooks/useCategories";
import { CategoryOut } from "@/domain/category.interface";
import { Enrollment } from "@/domain/enrollment.interface";

type EnrollProgramModalProps = {
  open: boolean;
  onClose: () => void;
  participantName?: string;
  enrollment?: Enrollment;
};

const DisenrollProgramModal: FC<EnrollProgramModalProps> = ({
  open,
  onClose,
  participantName,
  enrollment,
}) => {
  const { t } = useTranslation("clients");
  const { t: tCommon } = useTranslation("common");
  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const theme = useAppTheme();
  const { participantId } = useParams();
  const [reasonId, setReasonId] = useState<string>();
  const [reasonError, setReasonError] = useState<string | null>();

  const createAuditLog = useCreateAuditlog();
  const disenroll = useDisenroll();

  const { category, isLoadingCategory } = useCategoryByType({
    type: "DISENROLL_REASONS",
    status: "ACTIVE",
  });

  const reasons = useMemo(() => {
    if (!category) return [];

    return category.map((categoryElement: CategoryOut) => {
      return {
        key: categoryElement._id,
        value: categoryElement.name.def,
      };
    });
  }, [category]);

  const handleSubmit = async () => {
    if (!reasonId) {
      setReasonError(tCommon("validations.required"));
      return;
    } else {
      setReasonError("");
    }
    const payload = {
      participantId: participantId as string,
      enrollmentId: enrollment?._id ?? "",
      updatedBy: userInfo?._id as string,
      reason: reasonId,
    };

    await disenroll.mutateAsync(payload, {
      onSuccess: () => {
        onClose();
        showSnackBar(t("client_profile.disenroll-success"), "success");
        const programName = `${enrollment?.program?.name ?? ""}`;
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ENROLLMENTS,
          option: OPTIONS.ENROLLMENT,
          actionCode: ACTION_CODE.WEB_ENR_PART_DIS,
          action: "When disenrolling participant from a program",
          detail: `Disenrolled participant ${participantName} from program ${programName}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id || "",
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      },
      onError: (error: ErrorResponse) => {
        const message = Array.isArray(error.message)
          ? error.message[0]
          : error.message;
        showSnackBar(message || t("client_profile.disenroll-error"), "error");
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      title={t("client_profile.disenroll_title")}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 380,
        maxHeight: 400,
        maxWidth: 380,
      }}
      content={
        <Grid container spacing={2} mt={2}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="titleSmall"
              sx={{ fontWeight: 700, color: "#797D7D" }}
            >
              {participantName}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="bodyLarge">
              Are you sure you want to disenroll this user? Please select an
              option why:
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Select
              handleChange={(value) => {
                setReasonId(value as string);
                setReasonError("");
              }}
              isLoading={isLoadingCategory}
              data={reasons}
              value={reasonId}
              label={t("client_profile.reason")}
              id="program"
              fullWidth
              error={!!reasonError}
              errorMessage={reasonError ?? ""}
              required={true}
            />
          </Grid>
        </Grid>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={false}
            label={tCommon("cancel")}
            sx={{ textTransform: "uppercase" }}
          />
          <Button
            variantType="save"
            onClick={handleSubmit}
            disabled={disenroll.isPending}
            isLoading={disenroll.isPending}
            label={t("client_profile.disenroll")}
            sx={{ textTransform: "uppercase" }}
          />
        </Box>
      }
    />
  );
};

export default DisenrollProgramModal;
